import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,  // Agregar
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Block as BlockIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import PaymentStatusBadge from '../../components/PaymentStatusBadge';
import PartialPaymentDialog from '../../components/PartialPaymentDialog';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { studentService, paymentService } from '../../services/api';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};
interface PaymentRow {
  id: string | number;
  month: string;
  year: number | string;
  amount: number;
  payment_date?: string | null;  // puede ser null o undefined
  status: 'paid' | 'pending' | 'baja' | string;
  bank_reference?: string;
  // agrega otros campos si los tienes (student_id, notes, etc.)
}

const StudentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [student, setStudent] = useState<any>(null);
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMatrix, setPaymentMatrix] = useState<any[]>([]);

  useEffect(() => {
    loadStudentData();
  }, [id]);

  const [selectedPartialPayment, setSelectedPartialPayment] = useState<{
    open: boolean;
    month: string;
    year: number;
    totalDue: number;
    paidAmount: number;
    paymentId?: number;
  } | null>(null);

  const loadStudentData = async () => {
    try {
      setLoading(true);
      const [studentRes, paymentsRes] = await Promise.all([
        studentService.getById(parseInt(id!)),
        studentService.getPayments(parseInt(id!))
      ]);
      
      setStudent(studentRes.data);
      setPayments(paymentsRes.data.data || []);

      // 👈 CONSTRUIR PAYMENT MATRIX CON LA NUEVA LÓGICA
      if (studentRes.data.payment_status) {
        buildPaymentMatrixFromStatus(studentRes.data.payment_status);
      } else {
        // Fallback: usar la lógica anterior
        buildPaymentMatrix(paymentsRes.data.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'baja': return 'error';
      case 'finalizado': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'inactive': return 'Inactivo';
      case 'baja': return 'Baja';
      case 'finalizado': return 'Finalizado';
      default: return status;
    }
  };

  const getPaymentStatusIcon = (status: string | undefined): React.ReactElement | undefined => {
    switch (status) {
        case 'paid':
        return <CheckCircleIcon fontSize="small" color="success" />;
        case 'pending':
        return <HourglassEmptyIcon fontSize="small" color="warning" />;
        case 'baja':
        return <BlockIcon fontSize="small" color="error" />;
        default:
        return undefined;  // ← en lugar de null
    }
    };

 const paymentColumns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 70 },
  {
    field: 'month_year',
    headerName: 'Período',
    width: 120,
    renderCell: (params) => (
      <Typography variant="body2">
        {params.row.month} {params.row.year}
      </Typography>
    )
  },
  {
    field: 'amount',
    headerName: 'Monto Total',
    width: 100,
    renderCell: (params) => (
      <Typography variant="body2">
        ₽{(params.row.total_due || params.row.amount).toLocaleString()}
      </Typography>
    )
  },
  {
    field: 'paid_amount',
    headerName: 'Pagado',
    width: 100,
    renderCell: (params) => (
      <Typography variant="body2" fontWeight="medium" color="success.main">
        ₽{(params.row.paid_amount || 0).toLocaleString()}
      </Typography>
    )
  },
  {
    field: 'pending',
    headerName: 'Pendiente',
    width: 100,
    renderCell: (params) => {
      const total = params.row.total_due || params.row.amount;
      const paid = params.row.paid_amount || 0;
      const pending = total - paid;
      return (
        <Typography variant="body2" color={pending > 0 ? 'error.main' : 'success.main'}>
          ₽{pending.toLocaleString()}
        </Typography>
      );
    }
  },
  {
    field: 'progress',
    headerName: 'Progreso',
    width: 150,
    renderCell: (params) => {
      const total = params.row.total_due || params.row.amount;
      const paid = params.row.paid_amount || 0;
      const percentage = total > 0 ? (paid / total) * 100 : 0;
      return (
        <Box sx={{ width: '100%' }}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption">
              {percentage.toFixed(0)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={percentage}
            color={percentage >= 100 ? 'success' : percentage >= 50 ? 'warning' : 'error'}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      );
    }
  },
  {
    field: 'status',
    headerName: 'Estado',
    width: 150,
    renderCell: (params) => (
      <PaymentStatusBadge
        status={params.row.status}
        isCompleted={params.row.is_completed}
        percentage={params.row.total_due ? (params.row.paid_amount / params.row.total_due) * 100 : 100}
        paidAmount={params.row.paid_amount || 0}
        totalDue={params.row.total_due || params.row.amount}
      />
    )
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    width: 150,
    renderCell: (params) => (
      <Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => handlePartialPayment(params.row)}
          disabled={params.row.is_completed}
        >
          {params.row.paid_amount > 0 ? 'Agregar Pago' : 'Pagar'}
        </Button>
      </Box>
    )
  }
];

// Función para manejar pago parcial
const handlePartialPayment = (payment: any) => {
  setSelectedPartialPayment({
    open: true,
    month: payment.month,
    year: payment.year,
    totalDue: payment.total_due || payment.amount,
    paidAmount: payment.paid_amount || 0,
    paymentId: payment.id
  });
};
  const currentYear = new Date().getFullYear();
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const generatePaymentMatrix = () => {
    const matrix: any[] = [];
    const currentMonthIndex = new Date().getMonth();
    
    for (let i = 0; i < 12; i++) {
      const monthIndex = (currentMonthIndex - i + 12) % 12;
      const month = months[monthIndex];
      const year = currentYear - (monthIndex > currentMonthIndex ? 1 : 0);
      
      const payment = payments.find(p => 
        p.month === month && p.year === year
      );
      
      matrix.unshift({
        month,
        year,
        status: payment?.status || 'pending',
        payment: payment
      });
    }
    
    return matrix;
  };

  // 👈 NUEVO MÉTODO: Construir la matriz desde payment_status (que viene del backend)
  const buildPaymentMatrixFromStatus = (paymentStatus: any[]) => {
    // Agrupar por año y ordenar
    const grouped = paymentStatus.reduce((acc: any, item: any) => {
      const key = `${item.year}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    const sortedYears = Object.keys(grouped).sort((a, b) => parseInt(b) - parseInt(a));
    const matrix: any[] = [];

    sortedYears.forEach(year => {
      const months = grouped[year];
      // Ordenar meses dentro del año
      const monthOrder = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      months.sort((a: any, b: any) => monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month));
      
      months.forEach((item: any) => {
        matrix.push({
          ...item,
          // Asegurar que tenga las propiedades necesarias para el render
          is_excluded: item.is_excluded || false,
          is_within_period: item.is_within_period !== undefined ? item.is_within_period : true
        });
      });
    });

    setPaymentMatrix(matrix);
  };

  // 📌 MÉTODO FALLBACK: Si no viene payment_status del backend
  const buildPaymentMatrix = (paymentsList: any[]) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    
    // Determinar mes de inicio (desde fecha de ingreso o último año)
    let startYear = currentYear;
    let startMonth = 0; // Enero
    
    if (student?.enrollment_date) {
      const enrollmentDate = new Date(student.enrollment_date);
      startYear = enrollmentDate.getFullYear();
      startMonth = enrollmentDate.getMonth();
    } else {
      // Si no tiene fecha de ingreso, mostrar últimos 12 meses
      startYear = currentYear;
      startMonth = currentMonth - 11;
      if (startMonth < 0) {
        startMonth += 12;
        startYear--;
      }
    }
    
    const matrix: any[] = [];
    
    // Generar meses desde startMonth hasta currentMonth
    let year = startYear;
    let month = startMonth;
    
    while (year < currentYear || (year === currentYear && month <= currentMonth)) {
      const monthName = months[month];
      const payment = paymentsList.find(p => 
        p.month === monthName && p.year === year
      );
      
      matrix.push({
        year,
        month: monthName,
        month_number: month + 1,
        status: payment?.status || 'pending',
        has_payment: !!payment,
        payment: payment || null,
        is_excluded: false,
        is_within_period: true
      });
      
      month++;
      if (month > 11) {
        month = 0;
        year++;
      }
    }
    
    setPaymentMatrix(matrix);
  };

  // 👈 FUNCIÓN PARA RENDERIZAR EL CHIP DE ESTADO
  const renderStatusChip = (item: any) => {
    if (item.is_excluded) {
      return (
        <Chip
          label="Vacaciones"
          size="small"
          variant="outlined"
          sx={{ width: '100%', bgcolor: '#f5f5f5', color: '#999' }}
        />
      );
    }

    if (item.status === 'paid') {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Pagado"
          color="success"
          size="small"
          sx={{ width: '100%' }}
        />
      );
    }
    
    if (item.status === 'partial') {
      return (
        <Chip
          icon={<ScheduleIcon />}
          label="Parcial"
          color="warning"
          size="small"
          sx={{ width: '100%' }}
        />
      );
    }
    
    if (item.status === 'baja') {
      return (
        <Chip
          icon={<WarningIcon />}
          label="Baja"
          color="error"
          size="small"
          sx={{ width: '100%' }}
        />
      );
    }
    
    return (
      <Chip
        icon={<ErrorIcon />}
        label="Pendiente"
        color="default"
        size="small"
        sx={{ width: '100%' }}
      />
    );
  };

   // 👈 FUNCIÓN PARA OBTENER EL COLOR DE FONDO DEL CARD
  const getCardBgColor = (item: any) => {
    if (item.is_excluded) return '#f5f5f5';
    if (item.status === 'paid') return '#e8f5e9';
    if (item.status === 'partial') return '#fff3e0';
    if (item.status === 'baja') return '#ffebee';
    return '#fafafa';
  };

  // 👈 FUNCIÓN PARA OBTENER EL BORDER COLOR DEL CARD
  const getCardBorderColor = (item: any) => {
    if (item.is_excluded) return '#e0e0e0';
    if (item.status === 'paid') return '#4caf50';
    if (item.status === 'partial') return '#ff9800';
    if (item.status === 'baja') return '#f44336';
    return '#e0e0e0';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !student) {
    return (
      <Alert severity="error">
        {error || 'Estudiante no encontrado'}
      </Alert>
    );
  }

  //const paymentMatrix = generatePaymentMatrix();

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton component={Link} to="/students" sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {student.first_name} {student.last_name}
          </Typography>
          <Chip
            label={student.status === 'active' ? 'Activo' : 
                   student.status === 'exento' ? 'Exento' :
                   student.status === 'baja' ? 'Baja' : 
                   student.status === 'finalizado' ? 'Finalizado' : 'Inactivo'}
            color={student.status === 'active' ? 'success' : 
                   student.status === 'exento' ? 'warning' :
                   student.status === 'baja' ? 'error' : 'default'}
            sx={{ ml: 2 }}
          />
        </Box>
        
        <Box>
          <Button
            variant="outlined"
            startIcon={<PaymentIcon />}
            onClick={() => setPaymentDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Registrar Pago
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/students/${id}/edit`)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Editar
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Información General" />
          <Tab label="Historial de Pagos" />
          <Tab label="Estado Mensual" />
          <Tab label="Estadísticas" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Información del estudiante */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Información del Estudiante
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nombre Completo
                  </Typography>
                  <Typography variant="body1">
                    {student.first_name} {student.last_name}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Grado
                  </Typography>
                  <Typography variant="body1">
                    {student.grade}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Patrocinador
                  </Typography>
                  <Typography variant="body1">
                    {student.sponsor || 'No asignado'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Fecha de Ingreso
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(student.enrollment_date).format('D [de] MMMM [de] YYYY')}
                  </Typography>
                </Grid>
                {student.notes && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary">
                      Notas
                    </Typography>
                    <Typography variant="body1">
                      {student.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Información del padre */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Padre/Tutor
              </Typography>
              {student.parent ? (
                <>
                  <Typography variant="body1" gutterBottom>
                    {student.parent.spanish_name || student.parent.russian_name}
                  </Typography>
                  {student.parent.email && (
                    <Typography variant="body2" color="text.secondary">
                      Email: {student.parent.email}
                    </Typography>
                  )}
                  {student.parent.phone && (
                    <Typography variant="body2" color="text.secondary">
                      Teléfono: {student.parent.country_code} {student.parent.phone}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    component={Link}
                    to={`/parents/${student.parent.id}`}
                    sx={{ mt: 2 }}
                  >
                    Ver detalles del padre
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No asignado
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Estadísticas rápidas */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Estadísticas
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Total Pagado
                      </Typography>
                      <Typography variant="h5">
                        ${payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + parseFloat(p.amount), 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Pagos Realizados
                      </Typography>
                      <Typography variant="h5">
                        {payments.filter(p => p.status === 'paid').length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Meses Sin Pago
                      </Typography>
                      <Typography variant="h5" color={
                        student.months_without_payment >= 3 ? 'error' :
                        student.months_without_payment >= 2 ? 'warning' : 'success'
                      }>
                        {student.months_without_payment || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Último Pago
                      </Typography>
                      <Typography variant="h5">
                        {payments.filter(p => p.status === 'paid').length > 0 
                          ? dayjs(payments.filter(p => p.status === 'paid')[0].payment_date).format('MM/YYYY')
                          : 'Nunca'
                        }
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Historial de Pagos */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ height: 500 }}>
          <DataGrid
            rows={payments}
            columns={paymentColumns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            onRowClick={(params) => navigate(`/monthly-payments/${params.id}`)}
          />
        </Paper>
      </TabPanel>

      {/* Estado Mensual */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" gutterBottom>
              Estado de Pagos por Mes
            </Typography>
            {student.enrollment_date && (
              <Typography variant="caption" color="text.secondary">
                Desde: {dayjs(student.enrollment_date).format('DD/MM/YYYY')}
              </Typography>
            )}
          </Box>
          
          {paymentMatrix.length === 0 ? (
            <Alert severity="info">
              No hay meses disponibles para mostrar.
              {!student.enrollment_date && ' El estudiante no tiene fecha de ingreso registrada.'}
            </Alert>
          ) : (
            <Grid container spacing={1}>
              {paymentMatrix.map((item, index) => (
                <Grid size={{ xs: 6, sm: 4, md: 2 }} key={index}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      bgcolor: getCardBgColor(item),
                      borderColor: getCardBorderColor(item),
                      borderWidth: item.is_excluded ? 1 : 2,
                      opacity: item.is_excluded ? 0.7 : 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: item.is_excluded ? 'none' : 2,
                        transform: item.is_excluded ? 'none' : 'scale(1.02)'
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 1.5 }}>
                      <Typography 
                        variant="caption" 
                        display="block" 
                        color={item.is_excluded ? 'text.secondary' : 'text.primary'}
                        fontWeight={item.is_excluded ? 'normal' : 'medium'}
                      >
                        {item.month.charAt(0).toUpperCase() + item.month.slice(1)} {item.year}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {renderStatusChip(item)}
                      </Box>
                      {item.payment && !item.is_excluded && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                          ₽{item.payment.amount}
                        </Typography>
                      )}
                      {item.is_excluded && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                          Sin cobro
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          
          {/* Leyenda */}
          <Box display="flex" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 14, height: 14, bgcolor: '#e8f5e9', border: '2px solid #4caf50', borderRadius: 1 }} />
              <Typography variant="caption">Pagado</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 14, height: 14, bgcolor: '#fff3e0', border: '2px solid #ff9800', borderRadius: 1 }} />
              <Typography variant="caption">Parcial</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 14, height: 14, bgcolor: '#fafafa', border: '2px solid #e0e0e0', borderRadius: 1 }} />
              <Typography variant="caption">Pendiente</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 14, height: 14, bgcolor: '#ffebee', border: '2px solid #f44336', borderRadius: 1 }} />
              <Typography variant="caption">Baja</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 14, height: 14, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 1 }} />
              <Typography variant="caption">Vacaciones</Typography>
            </Box>
          </Box>
          
          {/* Resumen rápido */}
          <Box display="flex" flexWrap="wrap" gap={3} sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Pagado
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="success.main">
                ₽{paymentMatrix
                  .filter(p => p.status === 'paid' && !p.is_excluded)
                  .reduce((sum, p) => sum  + Number(p.payment?.amount ?? 0), 0)
                  .toLocaleString()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Meses Pagados
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="success.main">
                {paymentMatrix.filter(p => p.status === 'paid' && !p.is_excluded).length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Meses Pendientes
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="error.main">
                {paymentMatrix.filter(p => p.status === 'pending' && !p.is_excluded).length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Meses de Vacaciones
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="text.secondary">
                {paymentMatrix.filter(p => p.is_excluded).length}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </TabPanel>

      {/* Estadísticas */}
      <TabPanel value={tabValue} index={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Análisis Detallado
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom>
                Tendencia de Pagos
              </Typography>
              {/* Aquí iría un gráfico de tendencia */}
              <Box sx={{ height: 200, bgcolor: 'grey.100', borderRadius: 1 }} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom>
                Distribución por Estado
              </Typography>
              <List>
                <ListItem>
                  <ListItemText 
                    primary="Pagados" 
                    secondary={`${payments.filter(p => p.status === 'paid').length} pagos`}
                  />
                  <Chip label="Completado" color="success" size="small" />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Pendientes" 
                    secondary={`${payments.filter(p => p.status === 'pending').length} pagos`}
                  />
                  <Chip label="Pendiente" color="warning" size="small" />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Bajas" 
                    secondary={`${payments.filter(p => p.status === 'baja').length} pagos`}
                  />
                  <Chip label="Baja" color="error" size="small" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* Dialog para nuevo pago */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Registrar Nuevo Pago</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Estudiante: {student.first_name} {student.last_name}
          </Typography>
          {/* Aquí iría el formulario para registrar pago */}
          <Alert severity="info" sx={{ mt: 2 }}>
            Esta funcionalidad se implementará próximamente
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={() => {
              setPaymentDialogOpen(false);
              navigate('/monthly-payments/new', { state: { studentId: id } });
            }}
            variant="contained"
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
      {selectedPartialPayment && (
        <PartialPaymentDialog
          open={selectedPartialPayment.open}
          onClose={() => setSelectedPartialPayment(null)}
          onSuccess={loadStudentData}
          student={student}
          month={selectedPartialPayment.month}
          year={selectedPartialPayment.year}
          totalDue={selectedPartialPayment.totalDue}
          paidAmount={selectedPartialPayment.paidAmount}
          paymentId={selectedPartialPayment.paymentId}
        />
      )}
    </Box>
  );
};

export default StudentDetail;